import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { PriceListItem } from '@app/services/economy.service';
import { StockplaceItem } from '@app/services/logistic.service';
import { DynamicSpecialFunctionType, OrderheadService, OrderLineCalculation } from '@app/services/orderhead.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OrderHeadContextService } from '../order-head-context.service';
import { DataTaskError } from '@app/core/services/data-task.service';
import { ToastType } from '@app/core/components/toast/toast.component';

@Component({
    selector: 'tt-order-head-order-line-calculation',
    templateUrl: './order-head-order-line-calculation.component.html',
    styleUrls: ['./order-head-order-line-calculation.component.css'],
    standalone: false,
})
export class OrderHeadOrderLineCalculationComponent implements OnChanges, OnDestroy, OnInit {
    @Input()
    public orderlineKeyno?: string | number | null;

    @Input()
    public refreshToken = 0;

    public calculation?: OrderLineCalculation;

    public pricelists: PriceListItem[] = [];

    public stockplaces: StockplaceItem[] = [];

    public loading = false;

    public runningFunction = false;

    private readonly destroy$ = new Subject<void>();

    private readToken = 0;

    private readonly calculationMessages: {
        noOrderLineSelected: CalculationMessage;
        calculationFailed: CalculationMessage;
        noCalculation: CalculationMessage;
        calculating: CalculationMessage;
    } = {
        noOrderLineSelected: { type: 'warning', text: 'select_orderline_to_calculate', translate: true } as CalculationMessage,
        calculationFailed: { type: 'danger', text: 'calculation_failed', translate: true } as CalculationMessage,
        noCalculation: { type: 'primary', text: 'no_calculation_for_selected_orderline', translate: true } as CalculationMessage,
        calculating: { type: 'primary', text: 'calculating_selected_orderline', translate: true } as CalculationMessage,
    };

    public calculationMessage?: CalculationMessage = { ...this.calculationMessages.noOrderLineSelected };

    constructor(
        private orderhead: OrderheadService,
        private context: OrderHeadContextService,
    ) {}

    public async read() {
        const token = this.readToken + 1;
        this.readToken = token;
        const orderlineKeyno = this.orderlineKeyno;

        if (!orderlineKeyno) {
            this.calculation = undefined;
            this.loading = false;
            this.setCalculationMessage(this.calculationMessages.noOrderLineSelected);
            return;
        }

        try {
            this.loading = true;
            this.setCalculationMessage(this.calculationMessages.calculating);
            const response = await this.orderhead.getOrderLineCalculation({ orderline_keyno: orderlineKeyno });

            if (token !== this.readToken || this.orderlineKeyno !== orderlineKeyno) return;

            const errorMessage = this.getResponseErrorMessage(response);

            if (!!errorMessage) {
                delete this.calculation;
                this.setCalculationFailedMessage(errorMessage);
                return;
            }

            if (!response) {
                delete this.calculation;
                this.setCalculationMessage(this.calculationMessages.noCalculation);
                return;
            }

            this.calculation = {
                ...this.createEmptyCalculation(orderlineKeyno),
                ...response,
                selected_orderline_keyno: orderlineKeyno,
            };
            this.clearCalculationMessage();
        } catch (error) {
            if (token !== this.readToken || this.orderlineKeyno !== orderlineKeyno) return;

            delete this.calculation;
            console.error(error);
            this.setCalculationFailedMessage(this.getErrorMessage(error));
        } finally {
            if (token === this.readToken) {
                this.loading = false;
            }
        }
    }

    public async calculateOrder() {
        await this.runFunction('special_f_vartdal_db2');
    }

    public async updateOrderPrices() {
        await this.runFunction('special_update_orderprices');
    }

    private async runFunction(specialFunction: DynamicSpecialFunctionType) {
        if (!this.calculation?.selected_orderline_keyno || this.runningFunction) {
            if (this.calculation) this.calculation.special_function = '';
            return;
        }

        const orderlineKeyno = this.calculation.selected_orderline_keyno;

        try {
            this.runningFunction = true;
            this.setCalculationMessage(this.calculationMessages.calculating);
            this.calculation.special_function = specialFunction;
            const response = await this.orderhead.callDynamicSpecialFunctions({ ...this.calculation, special_function: specialFunction });
            const errorMessage = this.getResponseErrorMessage(response);

            if (!!errorMessage) {
                this.setCalculationFailedMessage(errorMessage);
                return;
            }

            this.clearCalculationMessage();
            await this.read();
        } catch (error) {
            console.error(error);
            this.setCalculationFailedMessage(this.getErrorMessage(error));
        } finally {
            if (this.calculation) this.calculation.special_function = '';

            this.runningFunction = false;
        }
    }

    private setCalculationMessage(message: CalculationMessage) {
        // delete this.calculation;
        this.calculationMessage = { ...message };
    }

    private setCalculationFailedMessage(text: string) {
        this.calculationMessage = { ...this.calculationMessages.calculationFailed, text, translate: false };
    }

    private clearCalculationMessage() {
        this.calculationMessage = undefined;
    }

    private getResponseErrorMessage(response: unknown): string {
        const result = Array.isArray(response) ? response[0] : response;

        if (!result || typeof result !== 'object') return '';

        const errorResponse = result as { errorcode?: string | number; errormessage?: string; iserror?: boolean; isError?: boolean; message?: string };
        const hasErrorCode = errorResponse.errorcode !== undefined && errorResponse.errorcode !== null && '' + errorResponse.errorcode !== '0';
        const hasErrorFlag = errorResponse.iserror === true || errorResponse.isError === true;

        return hasErrorCode || hasErrorFlag ? errorResponse.errormessage || errorResponse.message || this.calculationMessages.calculationFailed.text : '';
    }

    private getErrorMessage(error: unknown): string {
        if (error instanceof DataTaskError) {
            return error.response?.message || error.message || this.calculationMessages.calculationFailed.text;
        }

        if (error instanceof Error) {
            return error.message || this.calculationMessages.calculationFailed.text;
        }

        return '' + (error || this.calculationMessages.calculationFailed.text);
    }

    private createEmptyCalculation(orderlineKeyno: string | number): OrderLineCalculation {
        return {
            selected_orderline_keyno: orderlineKeyno,
            pricelist_id: '',
            pricelist_id2: '',
            purchase_pricelist_id: '',
            purchase_pricelist_id2: '',
            stockplace_id: '',
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['orderlineKeyno'] || changes['refreshToken']) {
            this.read();
        }
    }

    ngOnInit(): void {
        this.context.pricelists$.pipe(takeUntil(this.destroy$)).subscribe((pricelists) => (this.pricelists = pricelists));
        this.context.stockplaces$.pipe(takeUntil(this.destroy$)).subscribe((stockplaces) => (this.stockplaces = stockplaces));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

interface CalculationMessage {
    type: ToastType;
    text: string;
    translate: boolean;
}
